import { sanitizeRepositoryUrlOrNull, type DocumentSourceProvenance } from '@agentwiki/shared'
import { formatSourceType } from './document-kind'

export interface DocumentSourceIdentity {
  key: string
  label: string
  kindLabel: string
}

function decodeLabel(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return null
  }
}

/** Deterministic 64-bit FNV-1a; browser state receives only this opaque fingerprint. */
function fingerprint(value: string) {
  let hash = 0xcbf29ce484222325n
  for (const byte of new TextEncoder().encode(value)) {
    hash ^= BigInt(byte)
    hash = BigInt.asUintN(64, hash * 0x100000001b3n)
  }
  return hash.toString(16).padStart(16, '0')
}

function repositoryIdentity(repoUrl: string | null) {
  if (!repoUrl) return null
  if (repoUrl.startsWith('urn:agentwiki:')) return repoUrl
  return sanitizeRepositoryUrlOrNull(repoUrl)
}

function vaultNamespace(sourcePath: string) {
  const match = /^vault\/([^/]+)(?:\/|$)/.exec(sourcePath)
  return match?.[1] ?? null
}

export function getDocumentSourceIdentity(
  source: Pick<DocumentSourceProvenance, 'sourceType' | 'sourcePath' | 'repoUrl'>,
): DocumentSourceIdentity {
  if (source.sourceType === 'repo') {
    const stableRepository = repositoryIdentity(source.repoUrl)
    const opaque = fingerprint(stableRepository ?? 'repository-unavailable')
    return {
      key: `repo:${opaque}`,
      label: stableRepository
        ? `Repository ${opaque.slice(0, 8)}`
        : 'Repository identity unavailable',
      kindLabel: 'Repository',
    }
  }

  if (source.sourceType === 'vault') {
    const namespace = vaultNamespace(source.sourcePath)
    const opaque = fingerprint(namespace ?? 'vault-unavailable')
    return {
      key: `vault:${opaque}`,
      label: namespace
        ? decodeLabel(namespace) || 'Vault namespace unavailable'
        : 'Vault namespace unavailable',
      kindLabel: 'Vault',
    }
  }

  const opaque = fingerprint(source.repoUrl ?? source.sourceType)
  return {
    key: `${source.sourceType}:${opaque}`,
    label: formatSourceType(source.sourceType),
    kindLabel: 'Source',
  }
}

export function sourceIdentityMatches(
  source:
    | Pick<DocumentSourceProvenance, 'sourceType' | 'sourcePath' | 'repoUrl'>
    | null
    | undefined,
  sourceKey: string,
) {
  if (!source) return false
  if (getDocumentSourceIdentity(source).key === sourceKey) return true

  // Read legacy raw selectors for bookmarked compatibility, but never serialize them again.
  if (source.sourceType === 'repo') return sourceKey === `repo:${source.repoUrl ?? 'unknown'}`
  if (source.sourceType === 'vault') {
    const namespace = vaultNamespace(source.sourcePath)
    return Boolean(namespace && sourceKey === `vault:${namespace}`)
  }
  return false
}

export function formatSourceRevision(
  source: Pick<DocumentSourceProvenance, 'branch' | 'commitSha'>,
) {
  const branch = source.branch?.trim()
  const commit = source.commitSha?.trim().slice(0, 7)
  if (branch && commit) return `${branch} · ${commit}`
  return branch || commit || 'No revision metadata'
}
